import InformationIcon from '@/assets/icons/information.svg';
import JenLogoIcon from '@/assets/icons/jenLogo.svg';
import SideBarDiscoverIcon from '@/assets/icons/sideBarDiscover.svg';
import SideBarGenerateIcon from '@/assets/icons/sideBarGenerate.svg';
import SuperVoiceIcon from '@/assets/icons/superVoice.svg';
import SideBarHomeIcon from '@/assets/icons/sideBarHome.svg';
import SideBarMarketIcon from '@/assets/icons/sideBarMarket.svg';
import SideBarStylefilterIcon from '@/assets/icons/sideBarStylefilter.svg';
import UpgradeIcon from '@/assets/icons/upgrade.svg';
import { useDimensions } from '@/hooks/useDimensions';
import useInitializeData from '@/hooks/useInitializeProject';
import { useSessionStore } from '@/hooks/useSessionStore';
import { navList } from '@/pages/Home/components/Catalog';
import { ProfileModalContent } from '@/pages/Home/components/Profile';
import useSidebarStore from '@/state/sideBarStore';
import { cn, getInitialName } from '@/utils';
import { BookIcon, ChevronRight, ExternalLinkIcon, LogOut, UserIcon } from 'lucide-react';
import React, { useCallback, useEffect } from 'react';
import { NavLink, NavLinkProps, useLocation, useNavigate } from 'react-router-dom';
import { Dialog } from '../Dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../DropdownMenu';
import { useDarkModeStore } from '@/hooks/useDarkModeStore';
import { FEATURE_FLAG_CONFIG } from '@/config';
import useFlags from '@jenmusic/feature-flag';

const SideBar = ({ clickNavigationCallback }: { clickNavigationCallback?: () => void }) => {
  const { fetchData, session } = useSessionStore();

  const haveToolsPermission = session?.user?.permissions?.some(({ code }) => code === 'SHOW_TOOLS');
  const { showSuperVoice } = useFlags<typeof FEATURE_FLAG_CONFIG>();

  useEffect(() => {
    fetchData();
  }, []);
  const location = useLocation();
  const MY_CATALOG_PATH = navList?.map(({ to }) => {
    return to;
  });
  const { isSidebarOpen, changeSidebarState: changeGlobalSIderState } = useSidebarStore();

  const { isDarkMode, isMobileOrPad } = useInitializeData();

  const { toggleDarkMode } = useDarkModeStore();

  const inJeneratePath = MY_CATALOG_PATH.includes(location.pathname) || location.pathname.includes('/folder');
  const inSuperVoiceCreatePath = location.pathname === '/super_voice/create' || location.pathname.includes('/super_voice/track_create');

  const changeSidebarState = useCallback(
    (state: boolean) => {
      if (inJeneratePath || inSuperVoiceCreatePath) {
        changeGlobalSIderState(state);
      }
    },
    [inJeneratePath, inSuperVoiceCreatePath],
  );

  useEffect(() => {
    if (!inJeneratePath && !inSuperVoiceCreatePath) {
      changeGlobalSIderState(true);
    } else {
      changeGlobalSIderState(false);
    }
  }, [inJeneratePath, inSuperVoiceCreatePath, changeGlobalSIderState]);

  return (
    <div
      className={`bg-jenmusic-background-secondary h-full p-3 flex flex-col justify-between border-r border-jenmusic-border-background `}
      onMouseEnter={(e) => {
        changeSidebarState(true);
      }}
      onMouseLeave={() => {
        changeSidebarState(false);
      }}
    >
      <div className="flex flex-col gap-[38px] p-3">
        {isSidebarOpen ? (
          <JenLogoIcon className="w-[54px] h-[40px]" />
        ) : (
          <div className="w-[54px] h-[40px]">
            <div className="w-7 h-7 bg-jenmusic-interactive rounded-full"></div>
          </div>
        )}

        <div
          className="flex flex-col gap-3"
          onClick={() => {
            if (clickNavigationCallback) {
              clickNavigationCallback();
            }
          }}
        >
          <EnhancedNavLink to={'/home'} Icon={SideBarHomeIcon}>
            Home
          </EnhancedNavLink>
          <EnhancedNavLink to={'/'} Icon={SideBarGenerateIcon} showHighLight={inJeneratePath}>
            Jenerate
          </EnhancedNavLink>
          {showSuperVoice && (
            <EnhancedNavLink to={'/super_voice'} Icon={SuperVoiceIcon}>
              SuperVoice
            </EnhancedNavLink>
          )}
          <EnhancedNavLink to={'/discover'} Icon={SideBarDiscoverIcon}>
            Discover
          </EnhancedNavLink>
          <EnhancedNavLink to={'/marketplace'} Icon={SideBarMarketIcon}>
            <span className="flex">
              Marketplace
              {/* <sup className=" inline text-[10px] scale-75 top-[2px]">TM</sup> */}
            </span>
          </EnhancedNavLink>
          {haveToolsPermission && (
            <EnhancedNavLink to={'/style-filter-admin'} Icon={SideBarStylefilterIcon}>
              <div className="flex justify-between flex-1 items-center">
                Tools
                <div className=" font-pressura-mono text-jenmusic-background-primary text-[12px] tracking-[-0.36px] leading-[12px] h-[16px] flex items-center bg-[#E9B149] px-[6px] py-[4px] rounded-full">
                  PRO
                </div>
              </div>
            </EnhancedNavLink>
          )}
        </div>
      </div>
      <ProfileSettingsDropdownContainer changeSidebarState={changeSidebarState}>
        <Profile />
      </ProfileSettingsDropdownContainer>
    </div>
  );
};

const Profile = () => {
  const sessionStore = useSessionStore();
  const user = sessionStore?.session?.user;

  const { isSidebarOpen } = useSidebarStore();

  return (
    <div
      className={cn('flex justify-between items-center rounded-md hover:bg-[#00000008] p-[6px] cursor-pointer ml-1', {
        'w-fit': !isSidebarOpen,
      })}
    >
      <div className="flex gap-2">
        <div
          className={cn(
            `w-[32px] h-[32px] bg-jenmusic-background-primary rounded-md border border-jenmusic-border-primary text-jenmusic-content-secondary text-[14px] flex justify-center items-center `,
          )}
        >
          {!!user?.name && getInitialName(user?.name)}
        </div>
        {isSidebarOpen && (
          <div>
            <div className=" max-w-[150px] truncate text-left text-jenmusic-content-primary text-[16px] leading-none tracking-[-0.32px]">
              {user?.name}
            </div>
            <div className=" max-w-[150px] truncate text-left text-jenmusic-content-secondary text-[16px] leading-none tracking-[-0.23px]">
              {user?.email}
            </div>
          </div>
        )}
      </div>
      {isSidebarOpen && (
        <span className=" text-jenmusic-content-secondary">
          <ChevronRight width={16} color="currentColor" />
        </span>
      )}
    </div>
  );
};

const ProfileSettingsDropdownContainer = ({
  children,
  changeSidebarState,
}: {
  children: React.ReactNode;
  changeSidebarState: (state: boolean) => void;
}) => {
  const navigate = useNavigate();
  const { isMobileOrPad } = useDimensions();

  return (
    <div>
      <DropdownMenu>
        <DropdownMenuTrigger
          onMouseEnter={(e) => e.stopPropagation()}
          onMouseLeave={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          className="w-full"
        >
          {children}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          onCloseAutoFocus={() => changeSidebarState(false)}
          onClick={() => changeSidebarState(false)}
          onMouseEnter={(e) => e.stopPropagation()}
          onMouseLeave={(e) => e.stopPropagation()}
          side={isMobileOrPad ? 'top' : 'right'}
          align="end"
          className="bg-jenmusic-background-primary border-jenmusic-border-primary rounded-xl text-jenmusic-content-secondary w-[248px] p-[6px]"
        >
          <DropdownMenuItem
            className="flex gap-3 "
            onClick={() => {
              Dialog.info({
                dialogContentClassName: 'bg-[#EFEFF0] w-[568px]  lg:max-w-[568px]',
                contentJSX: ({ onChange }) => {
                  return <ProfileModalContent />;
                },
              });
            }}
          >
            <UserIcon width={16} /> <span className="flex-grow">Account Settings</span>
          </DropdownMenuItem>
          <DropdownMenuItem className="flex gap-3 " onClick={() => navigate('/recharge')}>
            <UpgradeIcon fillColor="currentColor" width={16} /> <span className="flex-grow">Manage Subscription</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator className="bg-jenmusic-border-primary mx-3 my-2" />

          <DropdownMenuItem className="flex gap-3 " onClick={() => window.open('https://support.jenmusic.ai/en/', '_blank')}>
            <InformationIcon fillColor="currentColor" width={16} /> <span className="flex-grow">Help and Support</span>
            <ExternalLinkIcon width={16} />
          </DropdownMenuItem>
          <DropdownMenuItem className="flex gap-3 " onClick={() => window.open('https://www.jenmusic.ai/api', '_blank')}>
            <BookIcon width={16} /> <span className="flex-grow">API & Documentation</span> <ExternalLinkIcon width={16} />
          </DropdownMenuItem>
          <DropdownMenuSeparator className="bg-jenmusic-border-primary mx-3 my-2" />
          <DropdownMenuItem className="flex gap-3 " onClick={() => window.location.replace('/logout')}>
            <LogOut color="currentColor" width={16} /> <span className="flex-grow">Log Out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

interface EnhancedNavLinkProps extends NavLinkProps {
  // className?: string;
  Icon: React.FC<any>;
  showHighLight?: boolean;
}

const EnhancedNavLink: React.FC<EnhancedNavLinkProps> = ({ className, showHighLight, Icon, ...props }) => {
  const { isSidebarOpen, toggleSidebar } = useSidebarStore();

  const navLinkClass =
    'min-w-[100px] text-jenmusic-content-primary flex gap-3 text-[20px] leading-6 tracking-[-0.8px] hover:text-jenmusic-content-secondary';

  return (
    <NavLink
      {...props}
      className={({ isActive }) => cn(`${navLinkClass} `, { ['!text-jenmusic-interactive']: isActive || showHighLight }, `${className}`)}
    >
      <Icon fillColor="currentColor" className="flex-shrink-0" />
      {isSidebarOpen && (props.children as React.ReactNode)}
    </NavLink>
  );
};

export default SideBar;
